import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Text,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button
} from '@tonic/shared/components/ui';
import { fetchGraphQL } from '../../../../lib/graphql';
import { Search, Plus, Users } from 'lucide-react';

const GET_CUSTOMER_SEGMENTS = `
  query GetCustomerSegments($filter: SegmentFilter) {
    customerSegments(filter: $filter) {
      id
      name
      description
      criteria {
        type
        value
      }
      customerCount
      createdAt
      updatedAt
    }
  }
`;

export function CustomerSegmentsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const { isLoading, error, data } = useQuery({
    queryKey: ['customerSegments', searchQuery, filterType],
    queryFn: () => fetchGraphQL(GET_CUSTOMER_SEGMENTS, {
      filter: {
        searchTerm: searchQuery || undefined,
        type: filterType === 'all' ? undefined : filterType
      }
    })
  });

  const handleCreateSegment = () => {
    navigate('/admin/customers/segments/new');
  };

  const handleSegmentClick = (id) => {
    navigate(`/admin/customers/segments/${id}`);
  };

  const getSegmentTypeColor = (criteria) => {
    const type = criteria[0]?.type;
    switch (type) {
      case 'PURCHASE_BEHAVIOR':
        return 'default';
      case 'LOYALTY_TIER':
        return 'secondary';
      case 'RECOMMERCE_ACTIVITY':
        return 'default';
      case 'ENGAGEMENT':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatCriteria = (criteria) => {
    if (!criteria?.length) return 'No criteria defined';

    return criteria.map(c => {
      switch (c.type) {
        case 'PURCHASE_BEHAVIOR':
          return `Orders ${c.value}`;
        case 'LOYALTY_TIER':
          return `${c.value} tier`;
        case 'RECOMMERCE_ACTIVITY':
          return `${c.value} re-commerce`;
        case 'ENGAGEMENT':
          return `${c.value} engagement`;
        default:
          return c.value;
      }
    }).join(', ');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <Text variant="h1">Customer Segments</Text>
          <Text color="muted">Create and manage customer segments</Text>
        </div>
        <Button onClick={handleCreateSegment}>
          <Plus className="h-4 w-4 mr-2" />
          Create Segment
        </Button>
      </div>

      <Card>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search segments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={filterType}
              onValueChange={setFilterType}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="PURCHASE_BEHAVIOR">Purchase Behavior</SelectItem>
                <SelectItem value="LOYALTY_TIER">Loyalty Tier</SelectItem>
                <SelectItem value="RECOMMERCE_ACTIVITY">Re-Commerce Activity</SelectItem>
                <SelectItem value="ENGAGEMENT">Engagement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              Error loading segments: {error.message}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Criteria</TableHead>
                  <TableHead>Customers</TableHead>
                  <TableHead>Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.customerSegments.map((segment) => (
                  <TableRow
                    key={segment.id}
                    onClick={() => handleSegmentClick(segment.id)}
                    className="cursor-pointer hover:bg-accent"
                  >
                    <TableCell>{segment.name}</TableCell>
                    <TableCell>{segment.description}</TableCell>
                    <TableCell>
                      <Badge variant={getSegmentTypeColor(segment.criteria)}>
                        {formatCriteria(segment.criteria)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {segment.customerCount.toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(segment.updatedAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 
